import type { BlockDef, BlockRefEntity, CadEntity } from '../model/entity';
import { entityBox, transformBlockChild, translateEntity } from '../model/entity';
import { newEntityId } from '../model/document';
import { boxUnion, type Pt } from '../model/geom';
import {
  footprintSquareMeters,
  generateBlockGeometry,
  normalizeParams,
  shelfLengthMeters,
  type BlockParams,
  type ParametricSpec,
} from './parametric';

/**
 * 블록 카탈로그 (작업지시서 P4-03 ~ P4-06)
 *
 * DB(cad_block)의 블록 메타데이터와 편집기의 도형 정의를 잇는다.
 *
 * 두 종류를 같은 인터페이스로 다룬다:
 *   · 정적 블록   — 도형이 `geometry_json` 에 그대로 들어 있다 (소화전, 기둥 등)
 *   · 파라메트릭 — 규격만 저장하고 도형은 생성기가 만든다 (곤돌라, 쇼케이스 등)
 */

export interface CatalogBlock {
  code: string;
  name: string;
  categoryCode: string;
  /** 파라메트릭이면 스펙, 정적이면 undefined */
  spec?: ParametricSpec;
  /** 정적 블록의 도형 */
  staticEntities?: CadEntity[];
  defaultLayer: string;
  /** 정적 블록의 그린 크기(mm) — resizable 블록이 삽입 시 배율을 계산하는 기준 */
  widthMm?: number;
  depthMm?: number;
  /** 정적 블록을 삽입할 때 크기(배율)를 바꿀 수 있게 할지 — 등록 시 사용자가 고른다 */
  resizable?: boolean;
  /** 이격 요구 (mm) — 통로 폭 검증(P5-02)이 이 값으로 동작한다 */
  clearance?: { frontMm?: number; sideMm?: number; rearMm?: number };
  /** 단가 — 리뉴얼 물량 산출(P5-05)이 견적서가 되는 지점 */
  cost?: { standardKrw?: number; installKrw?: number };
  utility?: { power?: number | null; water?: boolean; drain?: boolean; heatLoadW?: number };
  assetErpCode?: string | null;
}

/* -------------------------------------------------------------------------- *
 * 삽입
 * -------------------------------------------------------------------------- */

export interface InsertOptions {
  at: { x: number; y: number };
  rotation?: number;
  params?: BlockParams;
  layer?: string;
  attributes?: Record<string, string>;
  /** 균등 배율. resizable 정적 블록의 크기 조정에 쓴다 (기본 1 = 그린 크기 그대로) */
  scale?: number;
}

/**
 * 블록 참조 엔티티를 만든다.
 *
 * 파라미터는 `attributes` 에 문자열로 담는다 — DB 스키마가 이미
 * 문자열 맵이고, DXF INSERT 속성도 문자열이라 왕복이 안전하다.
 */
export function makeBlockRef(block: CatalogBlock, opt: InsertOptions): BlockRefEntity {
  const attributes: Record<string, string> = { ...(opt.attributes ?? {}) };

  if (block.spec) {
    const p = normalizeParams(block.spec, opt.params);
    for (const [k, v] of Object.entries(p)) attributes[`p:${k}`] = String(v);
  }

  return {
    id: newEntityId(),
    type: 'blockref',
    layer: opt.layer ?? block.defaultLayer,
    blockCode: block.code,
    at: { x: Math.round(opt.at.x), y: Math.round(opt.at.y) },
    rotation: opt.rotation ?? 0,
    scale: opt.scale ?? 1,
    attributes,
  };
}

/** 블록 참조에 담긴 파라미터를 되읽는다 */
export function readParams(ref: BlockRefEntity): BlockParams {
  const out: BlockParams = {};
  for (const [k, v] of Object.entries(ref.attributes)) {
    if (!k.startsWith('p:')) continue;
    const n = Number(v);
    if (Number.isFinite(n)) out[k.slice(2)] = n;
  }
  return out;
}

/**
 * 블록 참조로부터 실제 도형 정의를 만든다.
 *
 * 파라메트릭 블록은 참조마다 파라미터가 다르므로 정의도 참조마다 달라진다.
 * → 문서의 `blocks` 에는 **파라미터 조합별로** 정의를 넣는다.
 *   같은 조합이면 같은 코드를 쓰므로 중복되지 않는다.
 */
export function resolveBlockDef(block: CatalogBlock, params: BlockParams): BlockDef {
  if (!block.spec) {
    return {
      code: block.code,
      name: block.name,
      entities: block.staticEntities ?? [],
      clearance: block.clearance,
      widthMm: block.widthMm,
      depthMm: block.depthMm,
    };
  }

  const p = normalizeParams(block.spec, params);
  return {
    code: variantCode(block.code, p),
    name: `${block.name} ${describeParams(p)}`,
    entities: generateBlockGeometry(block.spec, p, block.defaultLayer),
    clearance: block.clearance,
    widthMm: p.width,
    depthMm: p.depth,
  };
}

/**
 * 파라미터 조합을 코드에 붙인다.
 * `GONDOLA@w900-d900-b3-s5` 처럼 사람이 읽을 수 있게 둔다 —
 * DXF 로 내보냈을 때 다른 CAD 에서도 무엇인지 알아볼 수 있어야 한다.
 */
export function variantCode(baseCode: string, params: BlockParams): string {
  const keys = Object.keys(params).sort();
  if (keys.length === 0) return baseCode;
  const suffix = keys.map((k) => `${k[0]}${params[k]}`).join('-');
  return `${baseCode}@${suffix}`;
}

function describeParams(p: BlockParams): string {
  const bits: string[] = [];
  if (p.width) bits.push(`${p.width}`);
  if (p.depth) bits.push(`×${p.depth}`);
  if (p.bays && p.bays > 1) bits.push(`${p.bays}베이`);
  if (p.shelfLevels) bits.push(`${p.shelfLevels}단`);
  return bits.join(' ');
}

/* -------------------------------------------------------------------------- *
 * 분해 (EXPLODE)
 * -------------------------------------------------------------------------- */

/**
 * 블록 참조를 개별 도형으로 분해한다.
 *
 * 되돌릴 수 없는 작업이 아니다 — Undo 로 원상 복구된다.
 * 다만 분해하면 블록 속성(매대번호·구역)이 사라지므로 호출자가 경고해야 한다.
 */
export function explodeBlockRef(
  ref: BlockRefEntity,
  defs: Map<string, BlockDef>
): CadEntity[] {
  const def = defs.get(ref.blockCode);
  if (!def) return [];

  return def.entities.map((child) => ({
    ...transformBlockChild(child, ref),
    id: newEntityId(),
    // 블록의 레이어가 아니라 참조가 놓인 레이어를 물려준다.
    // 사용자가 레이어를 바꿔 배치했다면 그 의도를 지켜야 한다.
    layer: ref.layer,
  }));
}

/* -------------------------------------------------------------------------- *
 * 도형 → 블록 등록 (선택한 것을 그대로 블록으로)
 * -------------------------------------------------------------------------- */

export interface CapturedBlockGeometry {
  /** 원점(0,0) = 선택 전체의 외접 사각 좌하단으로 옮긴 도형. 새 ID 를 받는다. */
  entities: CadEntity[];
  widthMm: number;
  depthMm: number;
  /** 외접 사각 좌하단의 월드 좌표 — 새 블록 참조를 여기 그대로 놓으면 원래 자리와 같다. */
  origin: Pt;
}

/**
 * 선택한 도형을 블록 좌표계(원점 = 외접 사각 좌하단)로 옮긴다.
 *
 * 기존 정적 심볼(symbols.ts)과 같은 규약을 쓴다 — 그래야 나중에 INSERT 로
 * 다시 놓았을 때도 다른 블록과 똑같이 동작한다.
 */
export function captureSelectionAsBlock(
  selected: CadEntity[],
  blocks: Map<string, BlockDef>
): CapturedBlockGeometry {
  if (selected.length === 0) throw new Error('선택된 도형이 없습니다.');

  let box = entityBox(selected[0]!, blocks);
  for (const e of selected.slice(1)) box = boxUnion(box, entityBox(e, blocks));

  const origin = { x: box.minX, y: box.minY };
  const entities = selected.map((e) => ({ ...translateEntity(e, -origin.x, -origin.y), id: newEntityId() }));

  return {
    entities,
    widthMm: Math.max(1, Math.round((box.maxX - box.minX) / 1000)),
    depthMm: Math.max(1, Math.round((box.maxY - box.minY) / 1000)),
    origin,
  };
}

/* -------------------------------------------------------------------------- *
 * 집계 (P5-05 물량 산출 · P5-06 진열 지표의 입력)
 * -------------------------------------------------------------------------- */

export interface BlockTally {
  code: string;
  name: string;
  count: number;
  /** 진열 선반 총 연장 (m) */
  shelfLengthM: number;
  /** 바닥 점유 면적 (㎡) */
  footprintM2: number;
  /** 추정 비용 (원) */
  costKrw: number;
}

/**
 * 도면의 블록을 종류·규격별로 집계한다.
 * 리뉴얼 물량 산출과 진열 지표가 모두 이 함수를 쓴다.
 */
export function tallyBlocks(
  entities: CadEntity[],
  catalog: Map<string, CatalogBlock>
): BlockTally[] {
  const byKey = new Map<string, BlockTally>();

  for (const e of entities) {
    if (e.type !== 'blockref') continue;

    // 파라미터 변형(@...)이 붙어 있어도 원래 카탈로그 코드를 찾는다
    const baseCode = e.blockCode.split('@')[0]!;
    const block = catalog.get(baseCode);
    if (!block) continue;

    const params = readParams(e);
    const key = block.spec ? variantCode(baseCode, normalizeParams(block.spec, params)) : baseCode;

    const shelf = block.spec ? shelfLengthMeters(block.spec, params) : 0;
    const foot = block.spec ? footprintSquareMeters(block.spec, params) : 0;
    const unitCost = (block.cost?.standardKrw ?? 0) + (block.cost?.installKrw ?? 0);

    const prev = byKey.get(key);
    if (prev) {
      prev.count += 1;
      prev.shelfLengthM += shelf;
      prev.footprintM2 += foot;
      prev.costKrw += unitCost;
    } else {
      byKey.set(key, {
        code: key,
        name: block.spec
          ? `${block.name} ${describeParams(normalizeParams(block.spec, params))}`
          : block.name,
        count: 1,
        shelfLengthM: shelf,
        footprintM2: foot,
        costKrw: unitCost,
      });
    }
  }

  return [...byKey.values()].sort((a, b) => b.count - a.count);
}

export function buildCatalogMap(blocks: CatalogBlock[]): Map<string, CatalogBlock> {
  return new Map(blocks.map((b) => [b.code, b]));
}
